import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getWorkoutTypeColor, getWorkoutTypeName } from '../../utils/workoutColors';
import {
  formatDistance,
  getUnitPreference
} from '../../utils/unitConversions';
import WhoopRecoveryCard from './WhoopRecoveryCard';
import OuraReadinessCard from './OuraReadinessCard';
import ActivityMap from './ActivityMap';
import './HomePage.css';

const HomePage = ({ trainingPlan, profile, onPlanUpdate, onViewChange }) => {
  const { authFetch } = useAuth();
  const [unit] = useState(getUnitPreference);

  // Activity Feed State
  const [activityFeedType, setActivityFeedType] = useState('my'); // 'my' or 'following'
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);

  // Load activities based on feed type
  const loadActivities = useCallback(async () => {
    setActivitiesLoading(true);
    try {
      const endpoint = activityFeedType === 'my' ? '/api/feed/my' : '/api/feed';
      const res = await authFetch(`${endpoint}?limit=10`);
      if (res.ok) {
        const data = await res.json();
        setActivities(data.activities || []);
      }
    } catch (err) {
      console.error('Failed to load activities:', err);
    } finally {
      setActivitiesLoading(false);
    }
  }, [authFetch, activityFeedType]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  // Format date to YYYY-MM-DD string
  const formatDate = (date) => {
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  // Format race name for display
  const formatRaceName = (race) => {
    const names = {
      '5k': '5K',
      '10k': '10K',
      'half_marathon': 'Half Marathon',
      'half': 'Half Marathon',
      'marathon': 'Marathon',
      'ultra': 'Ultra Marathon',
      'ultra_marathon': 'Ultra Marathon'
    };
    return names[race] || race;
  };

  // Get next upcoming workout
  const nextWorkoutData = useMemo(() => {
    if (!trainingPlan?.weeks) return null;

    const today = formatDate(new Date());

    for (const week of trainingPlan.weeks) {
      for (const workout of week.workouts || []) {
        if (workout.date >= today && workout.type !== 'Rest') {
          return { workout, week };
        }
      }
    }
    return null;
  }, [trainingPlan]);

  // Get current week info (for retest week flag)
  const currentWeekInfo = useMemo(() => {
    if (!trainingPlan?.weeks) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (const week of trainingPlan.weeks) {
      const workouts = week.workouts || [];
      if (workouts.length === 0) continue;

      const dates = workouts.map(w => new Date(w.date));
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));

      if (today >= minDate && today <= maxDate) {
        return week;
      }
    }
    return trainingPlan.weeks[0];
  }, [trainingPlan]);

  // Live race countdown (updates every second)
  const [countdownTime, setCountdownTime] = useState(() => {
    const raceDate = profile?.goalRaceDate || profile?.goal_race_date || trainingPlan?.goal_race_date;
    if (!raceDate) return null;
    const diff = new Date(raceDate) - new Date();
    if (diff <= 0) return null;
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  });

  useEffect(() => {
    const raceDate = profile?.goalRaceDate || profile?.goal_race_date || trainingPlan?.goal_race_date;
    if (!raceDate) return;
    const tick = () => {
      const diff = new Date(raceDate) - new Date();
      if (diff <= 0) { setCountdownTime(null); return; }
      setCountdownTime({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [profile, trainingPlan]);

  // Format date for display
  const formatDisplayDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'short'
    });
  };

  const raceName = formatRaceName(profile?.goalRace || profile?.goal_race || trainingPlan?.goal_race);
  const raceYear = (() => {
    const d = profile?.goalRaceDate || profile?.goal_race_date || trainingPlan?.goal_race_date;
    return d ? new Date(d).getFullYear() : null;
  })();

  // Format activity date and time
  const formatActivityDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }) + ' at ' + date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="home-page">
      <div className="home-container">

        {/* Race Countdown */}
        {countdownTime && (
          <div className="race-countdown">
            <div className="countdown-circle">
              <span className="countdown-days">{countdownTime.days}</span>
              <span className="countdown-days-label">Days</span>
            </div>
            <div className="countdown-right">
              <div className="countdown-hms">
                <div className="countdown-unit">
                  <span className="countdown-value">{String(countdownTime.hours).padStart(2, '0')}</span>
                  <span className="countdown-label">HRS</span>
                </div>
                <span className="countdown-colon">:</span>
                <div className="countdown-unit">
                  <span className="countdown-value">{String(countdownTime.minutes).padStart(2, '0')}</span>
                  <span className="countdown-label">MIN</span>
                </div>
                <span className="countdown-colon">:</span>
                <div className="countdown-unit">
                  <span className="countdown-value">{String(countdownTime.seconds).padStart(2, '0')}</span>
                  <span className="countdown-label">SEC</span>
                </div>
              </div>
              <span className="countdown-until">Until Race Day</span>
            </div>
          </div>
        )}

        {/* Wearable Recovery & Readiness Cards */}
        <WhoopRecoveryCard />
        <OuraReadinessCard />

        {/* Activity Feed */}
        <div className="activity-feed-section">
          <div className="activity-feed-header">
            <div className="activity-feed-toggle">
              <button
                className={`toggle-btn ${activityFeedType === 'my' ? 'active' : ''}`}
                onClick={() => setActivityFeedType('my')}
              >
                My Activity
              </button>
              <button
                className={`toggle-btn ${activityFeedType === 'following' ? 'active' : ''}`}
                onClick={() => setActivityFeedType('following')}
              >
                Following
              </button>
            </div>
          </div>

          <div className="activity-feed-content">
            {activitiesLoading ? (
              <div className="activity-feed-loading">Loading activities...</div>
            ) : activities.length === 0 ? (
              <div className="activity-feed-empty">
                <div className="activity-feed-empty-icon">
                  {activityFeedType === 'my' ? '🏃' : '👥'}
                </div>
                <h4>
                  {activityFeedType === 'my'
                    ? 'No Activities Yet'
                    : 'No Activity from Following'}
                </h4>
                <p>
                  {activityFeedType === 'my'
                    ? 'Complete workouts to see your activity here'
                    : 'Follow other runners to see their activities'}
                </p>
                {activityFeedType === 'following' && (
                  <button
                    className="btn-primary"
                    onClick={() => onViewChange && onViewChange('community')}
                  >
                    Find Users
                  </button>
                )}
              </div>
            ) : (
              <div className="activity-feed-list">
                {activities.map((activity) => (
                  <div key={activity.id} className="activity-item">
                    <div className="activity-item-content">
                      <div className="activity-item-header">
                        <span className="activity-item-title">{activity.title}</span>
                      </div>
                      <span className="activity-item-time">{formatActivityDateTime(activity.created_at)}</span>
                      {activity.description && (
                        <p className="activity-item-description">{activity.description}</p>
                      )}
                      {activity.metadata?.garmin_activity_id && (
                        <ActivityMap activityId={activity.metadata.garmin_activity_id} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Next Workout */}
        <div className="home-card next-workout-standalone">
          <h3 className="card-title">Next Workout</h3>
          {nextWorkoutData ? (
            <>
              <div className="next-workout-card__date">
                {formatDisplayDate(nextWorkoutData.workout.date)}
              </div>
              <div className="next-workout-card__type">
                <span
                  className="workout-type-badge"
                  style={{ backgroundColor: getWorkoutTypeColor(nextWorkoutData.workout.type) }}
                >
                  {nextWorkoutData.workout.workout_name || getWorkoutTypeName(nextWorkoutData.workout.type)}
                </span>
              </div>
              {nextWorkoutData.workout.distance_km && (
                <div className="next-workout-card__distance">
                  <span className="distance-value">{formatDistance(nextWorkoutData.workout.distance_km, unit)}</span>
                  <span className="distance-unit">{unit}</span>
                </div>
              )}
              <p className="next-workout-card__description">
                {nextWorkoutData.workout.description}
              </p>
              {currentWeekInfo?.is_retest_week && (
                <div className="retest-note">Retest week</div>
              )}
            </>
          ) : (
            <p className="card-empty">No upcoming workouts</p>
          )}
        </div>

      </div>
    </div>
  );
};

export default HomePage;
